import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Award, Sparkles, GraduationCap, Tv, Users, Mic2, Clapperboard, Instagram } from "lucide-react";
import { FACULTY } from "@/lib/data";

export default function FacultyProfile() {
  const { id } = useParams();
  const member = FACULTY.find((f) => f.id === id);

  if (!member || member.placeholder) return <Navigate to="/instructors" replace />;

  return (
    <div data-testid={`faculty-profile-${member.id}`} className="bg-black">
      {/* Hero band */}
      <section className="relative pt-28 lg:pt-32 pb-12 border-b border-white/10">
        <div className="absolute inset-0 ember-bg opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <Link
            to="/instructors"
            data-testid="back-to-faculty"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white/55 hover:text-[#ff4500] transition-colors mb-10"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Founders & Our Team
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Photo */}
            <div className="lg:col-span-5">
              <div className="relative overflow-hidden border border-white/10 bg-[#0a0a0a]">
                <img
                  src={member.image}
                  alt={member.name}
                  className={`w-full h-full object-cover ${member.imagePosition || "object-top"} aspect-[4/5]`}
                />
              </div>
            </div>

            {/* Header info */}
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-3">◆ {member.role}</p>
              <h1 className="font-display text-4xl lg:text-6xl text-white leading-[0.95]">
                {member.name}
              </h1>
              {member.qualification && (
                <p className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/65 border border-white/15 px-4 h-9">
                  <GraduationCap className="w-4 h-4 text-[#ff4500]" /> {member.qualification}
                </p>
              )}
              {member.instagram && (
                <div className="mt-4">
                  <a
                    href={`https://instagram.com/${member.instagram}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`faculty-ig-${member.id}`}
                    className="inline-flex items-center gap-2 px-4 h-10 border border-[#ffb700]/40 text-[#ffb700] hover:bg-[#ffb700] hover:text-black hover:border-[#ffb700] transition-colors text-sm font-semibold w-fit"
                  >
                    <Instagram className="w-4 h-4" /> @{member.instagram}
                  </a>
                </div>
              )}
              <p className="mt-7 text-base lg:text-lg text-white/70 leading-relaxed max-w-2xl">
                {member.short}
              </p>

              {/* Stats row */}
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/5 max-w-2xl">
                <div className="bg-[#0a0a0a] p-5">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Experience</p>
                  <p className="font-display text-2xl phoenix-text mt-1">{member.years}+ years</p>
                </div>
                <div className="bg-[#0a0a0a] p-5">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Specialties</p>
                  <p className="font-display text-2xl text-white mt-1">{member.styles.length}</p>
                </div>
                {member.studentsTrained ? (
                  <div className="bg-[#0a0a0a] p-5">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Students Trained</p>
                    <p className="font-display text-2xl phoenix-text mt-1">{member.studentsTrained}</p>
                  </div>
                ) : member.showCredits ? (
                  <div className="bg-[#0a0a0a] p-5">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">TV Credits</p>
                    <p className="font-display text-2xl text-white mt-1">{member.showCredits.length}</p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-20 lg:py-28 max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Bio */}
        {member.bio && (
          <div className="max-w-3xl space-y-5 mb-16">
            {member.bio.split("\n\n").map((p, i) => (
              <p key={i} className="text-base lg:text-lg text-white/75 leading-relaxed">{p}</p>
            ))}
          </div>
        )}

        {/* Specialties */}
        <div className="mb-16">
          <h2 className="font-display text-2xl lg:text-3xl text-white inline-flex items-center gap-3 mb-6">
            <Sparkles className="w-5 h-5 text-[#ff4500]" strokeWidth={1.5} />
            {member.nonTeaching ? "Core Expertise" : "Specialties"}
          </h2>
          <div className="flex flex-wrap gap-2">
            {member.styles.map((s, i) => (
              <span
                key={i}
                className="text-xs px-4 h-10 inline-flex items-center bg-[#ff4500]/10 border border-[#ff4500]/40 text-[#ff4500] font-medium uppercase tracking-wider"
              >
                {s}
              </span>
            ))}
          </div>
        </div>

        {/* TV Show Credits */}
        {member.showCredits && member.showCredits.length > 0 && (
          <div className="mb-16">
            <h2 className="font-display text-2xl lg:text-3xl text-white inline-flex items-center gap-3 mb-6">
              <Tv className="w-5 h-5 text-[#ff4500]" strokeWidth={1.5} />
              Television Choreography
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {member.showCredits.map((c, i) => (
                <div
                  key={i}
                  className="bg-[#0a0a0a] border border-white/10 p-6 hover:border-[#ff4500] hover:bg-[#0f0f0f] transition-colors"
                >
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#ffb700] mb-2">{c.role}</p>
                  <h4 className="font-display text-xl text-white leading-tight">{c.show}</h4>
                  <p className="text-sm text-white/55 mt-2">{c.channel}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Freelance Choreography For */}
        {member.freelanceFor && member.freelanceFor.length > 0 && (
          <div className="mb-16">
            <h2 className="font-display text-2xl lg:text-3xl text-white inline-flex items-center gap-3 mb-6">
              <Mic2 className="w-5 h-5 text-[#ff4500]" strokeWidth={1.5} />
              Freelance Choreography For
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {member.freelanceFor.map((f, i) => (
                <div
                  key={i}
                  className="bg-[#0a0a0a] border border-white/10 p-5 hover:border-[#ff4500] transition-colors"
                >
                  <p className="text-sm text-white font-medium leading-tight">{f}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Director & Performing Artist For */}
        {member.directorFor && member.directorFor.length > 0 && (
          <div className="mb-16">
            <h2 className="font-display text-2xl lg:text-3xl text-white inline-flex items-center gap-3 mb-6">
              <Clapperboard className="w-5 h-5 text-[#ffb700]" strokeWidth={1.5} />
              Director & Performing Artist
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {member.directorFor.map((d, i) => (
                <div
                  key={i}
                  className="bg-[#0a0a0a] border border-[#ffb700]/30 p-5 hover:border-[#ffb700] hover:bg-[#0f0f0f] transition-colors"
                >
                  <p className="text-sm text-white font-medium leading-tight">{d}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Worked With */}
        {member.workedWith && member.workedWith.length > 0 && (
          <div className="mb-16">
            <h2 className="font-display text-2xl lg:text-3xl text-white inline-flex items-center gap-3 mb-6">
              <Users className="w-5 h-5 text-[#ff4500]" strokeWidth={1.5} />
              Has Worked With
            </h2>
            <div className="flex flex-wrap gap-2">
              {member.workedWith.map((w, i) => (
                <span
                  key={i}
                  className="text-sm px-4 h-10 inline-flex items-center bg-[#0a0a0a] border border-white/15 text-white/80"
                >
                  {w}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 border-t border-white/10 pt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-2">
              ◆ {member.nonTeaching ? "Part of the PDA team" : "Train with our faculty"}
            </p>
            <p className="font-display text-2xl text-white">
              {member.nonTeaching
                ? `Working behind the scenes at PDA.`
                : `Want to learn from ${member.name.split(" ")[0]}?`}
            </p>
            <p className="text-sm text-white/65 mt-1">
              {member.nonTeaching
                ? "Powering our digital presence — website, branding, and online experience."
                : "Book a free trial class and meet our team in person or online."}
            </p>
          </div>
          {!member.nonTeaching && (
            <Link to="/book-trial">
              <button
                data-testid="faculty-book-trial"
                className="btn-phoenix bg-[#ff4500] text-white px-8 h-14 uppercase tracking-wider text-xs font-semibold inline-flex items-center gap-2 whitespace-nowrap"
              >
                Book Free Trial <Award className="w-4 h-4" />
              </button>
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}
